import {useEffect} from 'react';
import * as THREE from 'three';
import {RoomType} from 'components/customer/RoomPlanner/types';
import {useAppSelector, useAppDispatch} from 'store/customer';
import {
    getHiddenWall,
    getIsTopView,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {setHiddenWallWithRetry} from 'components/customer/RoomPlanner/helpers/hiddenWallRetry';

const useWallHider = (
    wallRefs: React.MutableRefObject<
        THREE.Mesh<
            THREE.ExtrudeGeometry,
            THREE.MeshStandardMaterial,
            THREE.Object3DEventMap
        >[]
    >,
    roomType: RoomType
) => {
    const dispatch = useAppDispatch();
    const hiddenWall = useAppSelector(getHiddenWall);
    const isTopView = useAppSelector(getIsTopView);

    useEffect(() => {
        return setHiddenWallWithRetry(
            dispatch,
            'rightWall, frontWall',
            () => wallRefs.current?.length > 0
        );
    }, [dispatch, wallRefs]);

    useEffect(() => {
        if (hiddenWall) {
            const hiddenWalls = hiddenWall
                .split(',')
                .map((wall) => wall.trim());

            if (hiddenWalls.length > 2) return;

            if (roomType === 'RECTANGULAR') {
                wallRefs.current.forEach((wall) => {
                    (wall.userData as {onShow: () => void})?.onShow();
                    if (
                        hiddenWalls.includes(wall.userData.id as string) &&
                        !isTopView
                    )
                        (wall.userData as {onHide: () => void})?.onHide();
                });
            }

            if (roomType === 'LSHAPED') {
                wallRefs.current.forEach((wall) => {
                    (wall.userData as {onShow: () => void})?.onShow();

                    const wallsToHide: string[] = [];

                    // For each hidden wall, determine which walls should also be hidden
                    hiddenWalls.forEach((hiddenWallId) => {
                        switch (hiddenWallId) {
                            case 'leftWall':
                                wallsToHide.push('leftWidthWall');
                                break;
                            case 'frontWall':
                                const isLeftMainWall =
                                    hiddenWalls && hiddenWalls.length > 1
                                        ? hiddenWalls[0] === 'leftWall'
                                        : false;

                                if (isLeftMainWall) {
                                    wallsToHide.push('leftDepthWall');
                                } else {
                                    wallsToHide.push('leftDepthWall');
                                    wallsToHide.push('leftWidth2Wall');
                                    wallsToHide.push('rightWidth2Wall');
                                }

                                break;
                            case 'rightWall':
                                const isBackMainWall =
                                    hiddenWalls && hiddenWalls.length > 1
                                        ? hiddenWalls[0] === 'backWall'
                                        : false;

                                if (isBackMainWall) {
                                    wallsToHide.push('rightDepthWall');
                                } else {
                                    wallsToHide.push('rightDepthWall');
                                    wallsToHide.push('rightWidth2Wall');
                                    wallsToHide.push('leftWidth2Wall');
                                }
                                break;
                            default:
                                wallsToHide.push('rightWidthWall');
                                break;
                        }
                    });

                    if (wallsToHide.includes(wall.userData.id as string))
                        (wall.userData as {onHide: () => void})?.onHide();
                });
            }
        }
    }, [hiddenWall, roomType, isTopView]);

    return {hiddenWall};
};

export default useWallHider;
